import type { Route } from '@angular/router';

import { EModalName } from '@rpgc-ai/shared-interfaces/enums/modal-name.enum';

import { authGuard, authGuardInverted } from './core/guards/auth.guard';
import { NoSsrModalGuard } from './core/guards/no-ssr-modal.guard';

export const modalRoutes: Route[] = [
  // Контекстное меню
  {
    path: 'context-menu',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/mobile-context-menu/mobile-context-menu.component').then((m) => m.MobileContextMenuComponent),
      },
    ],
  },
  // Языки
  {
    path: 'languages',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/languages-modal/languages-modal.component').then((m) => m.LanguagesModalComponent),
      },
    ],
  },
  // Авторизация
  {
    path: 'sign-in',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard, authGuardInverted],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/login-providers/login-providers.component').then((m) => m.LoginProvidersComponent),
      },
    ],
  },
  // Регистрация
  {
    path: 'sign-up',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    title: 'Create your account',
    canActivate: [NoSsrModalGuard, authGuardInverted],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/registration/registration.component').then((m) => m.RegistrationComponent),
      },
    ],
  },
  // Восстановление пароля
  {
    path: 'password-recovery',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    title: 'MODALS.PASSWORD_RECOVERY.TITLE',
    canActivate: [NoSsrModalGuard, authGuardInverted],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/password-recovery/password-recovery.component').then((m) => m.PasswordRecoveryComponent),
      },
    ],
  },
  // Сброс пароля
  {
    path: 'password-reset',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    title: 'MODALS.PASSWORD_RECOVERY.RESET_PASSWORD',
    canActivate: [NoSsrModalGuard, authGuardInverted],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/password-reset/password-reset.component').then((m) => m.PasswordResetComponent),
      },
    ],
  },
  {
    path: 'allow-nsfw-content',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/allow-nsfw-content/allow-nsfw-content.component').then((m) => m.AllowNsfwContentComponent),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  // Генерация аватара
  {
    path: 'generate-avatar',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/generate-avatar/generate-avatar.component').then((m) => m.GenerateAvatarComponent),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  {
    path: 'generate-background',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/generate-background/generate-background.component').then((m) => m.GenerateBackgroundComponent),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  {
    path: 'image-generation-settings',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/image-generation-settings/image-generation-settings.component').then(
            (m) => m.ImageGenerationsSettingsComponent,
          ),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  {
    path: 'model-prompt-creator',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/model-prompt-creator/model-prompt-creator.component').then((m) => m.ModelPromptCreatorComponent),
        data: {
          preset: false,
        },
      },
      {
        path: 'preset',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/model-prompt-creator/model-prompt-creator.component').then((m) => m.ModelPromptCreatorComponent),
        data: {
          preset: true,
        },
      },
      {
        path: 'for-new-chatbot',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/model-prompt-creator/model-prompt-creator.component').then((m) => m.ModelPromptCreatorComponent),
        data: {
          isNewChatbot: true,
        },
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  // Предупреждение перед удалением персоны
  {
    path: 'delete-persona-warning/:id',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/delete-persona-warning/delete-persona-warning.component').then(
            (m) => m.DeletePersonaWarningComponent,
          ),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  // Предупреждение перед удалением воспоминания
  {
    path: 'delete-memory-warning/:id',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/memories/delete-memory-warning/delete-memory-warning.component').then(
            (m) => m.DeleteMemoryWarningComponent,
          ),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  // Предупреждение перед удалением чата
  {
    path: 'delete-chat-warning/:id',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/delete-chat-warning/delete-chat-warning.component').then((m) => m.DeleteChatWarningComponent),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  // Предупреждение перед удалением chatbot
  {
    path: 'delete-chatbot-warning/:botId',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/warning-delete-chatbot/warning-delete-chatbot.component').then(
            (m) => m.WarningDeleteChatbotComponent,
          ),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  {
    path: 'delete-chatbot-warning/:creatorSlug/:botId',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/warning-delete-chatbot/warning-delete-chatbot.component').then(
            (m) => m.WarningDeleteChatbotComponent,
          ),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  {
    path: 'chatbot-deleted',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/chatbot-remover-banner/chatbot-remover-banner.component').then(
            (m) => m.ChatBotRemovedBannerComponent,
          ),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  {
    path: 'chatbot-encrypted',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/chat-decryption-password/chat-decryption-password.component').then((m) => m.ChatDecryptionComponent),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  // QR-код google play
  {
    path: 'google-play-download',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/qr-code-modal/components/google-play-modal-content/google-play-modal-content.component').then(
            (m) => m.GooglePlayModalComponent,
          ),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  // QR-код app store
  {
    path: 'app-store-download',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/qr-code-modal/components/app-store-modal-content/app-store-modal-content.component').then(
            (m) => m.AppStoreModalComponent,
          ),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  {
    path: 'chat-encryption',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/chat-encryption/chat-encryption.component').then((m) => m.ChatEncryptionComponent),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  // share-wrapper
  {
    path: 'share-chatbot/:link',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    title: 'Share',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/share-bot-wrapper/share-bot-wrapper.component').then((m) => m.ShareBotModalWrapperComponent),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  // block-creator
  {
    path: 'block-creator/:id',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/warning-block-user/warning-block-user.component').then((m) => m.WarningBlockCreatorComponent),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },
  // unblock-creator
  {
    path: 'unblock/:id',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/unblock-creator/unblock-creator.component').then((m) => m.UnblockCreatorComponent),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },

  // Модалка с плейсхолдерами при создании бота
  {
    path: 'placeholder/:key',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/placeholder/placeholder-modal.component').then((m) => m.PlaceholderModalComponent),
      },
    ],
    canActivate: [NoSsrModalGuard],
  },

  // Email
  {
    path: 'login-email',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard, authGuardInverted],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/login-email/login-email.component').then((m) => m.LoginEmailComponent),
      },
    ],
  },
  // Модальные окна - главный контейнер
  {
    path: 'markdown',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    title: 'Документация',
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/markdown/markdown.component').then((m) => m.MarkdownComponent),
      },
    ],
  },
  // Payment modal
  {
    path: 'payment',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    title: '',
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/payment/payment-modal.component').then((m) => m.PaymentModalComponent),
      },
    ],
  },
  // Welcome modal
  {
    path: 'welcome',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    title: '',
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/welcome/welcome.component').then((m) => m.WelcomeComponent),
      },
    ],
  },
  // Voice modal
  {
    path: 'voice',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    title: 'Голос',
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/voice-changer/voice-changer.component').then((m) => m.VoiceChangerComponent),
      },
    ],
  },
  // Record-Keeping modal
  {
    path: 'recordKeep',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    title: '2257 Record-Keeping Requirements Compliance Statement',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/record-keeping-container/record-keeping-container.component').then(
            (m) => m.RecordKeepingModalComponent,
          ),
      },
    ],
  },
  // Community Guidelines Agreement modal
  {
    path: 'community-guidelines-agreement',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/community-guidelines-agreement/community-guidelines-agreement.component').then(
            (m) => m.CommunityGuidelinesAgreementComponent,
          ),
      },
    ],
  },
  // Character info modal
  {
    path: `${EModalName.CHATBOT_INFO}/:id`,
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/character-info/character-info.component').then((m) => m.CharacterInfoComponent),
      },
    ],
  },
  {
    path: `${EModalName.CHATBOT_INFO}/:creatorSlug/:botId`,
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/character-info/character-info.component').then((m) => m.CharacterInfoComponent),
      },
    ],
  },

  // Edit image modal
  {
    path: 'edit-image',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    title: 'Голос',
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/edit-image/edit-image.component').then((m) => m.EditImageComponent),
      },
    ],
  },
  // Tell us mode modal aka feedback
  {
    path: 'tell-us-more/:id/:rating',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    title: 'Tell Us More',
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/tell-us-more/tell-us-more.component').then((m) => m.TellUsMoreComponent),
      },
    ],
  },
  // Report user modal
  {
    path: 'report-profile/:id',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/complain/complain.component').then((m) => m.ComplainComponent),
      },
    ],
  },
  // Report message modal
  {
    path: 'report-message/:id/:rating',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/complain/complain.component').then((m) => m.ComplainComponent),
      },
    ],
  },
  // Report chatbot modal
  {
    path: 'report-chatbot/:id',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/complain/complain.component').then((m) => m.ComplainComponent),
      },
    ],
  },
  // Memories
  {
    path: 'memories',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/memories/memories.component').then((m) => m.MemoriesComponent),
      },
    ],
  },
  // Model settings
  {
    path: 'model-settings/:modelId/:promptId',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/model-settings/model-settings.component').then((m) => m.ModelSettingsComponent),
      },
    ],
  },
  // Fullscreen profile pic
  {
    path: 'profile-pic/:id',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/fullscreen-profile-pic/fullscreen-profile-pic.component').then(
            (m) => m.FullscreenProfilePicComponent,
          ),
      },
    ],
  },
  // Настройки профиля
  {
    path: 'profile-settings',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/profile-settings/profile-settings.component').then((m) => m.ProfileSettingsComponent),
      },
    ],
  },
  // Модалка верификации почты
  {
    path: 'email-verification',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/email-verification/email-verification.component').then((m) => m.EmailVerificationComponent),
      },
    ],
  },
  // Модалка выбора персоны
  {
    path: 'choose-persona',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/choose-persona/choose-persona.component').then((m) => m.ChoosePersonaComponent),
      },
    ],
  },
  {
    path: 'import-chatbot',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/import-chatbot/import-chatbot.component').then((m) => m.ImportChatbotComponent),
      },
    ],
  },
  {
    path: 'confirm-redirect/:destination',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/confirm-redirect-modal/confirm-redirect-modal.component').then(
            (m) => m.ConfirmRedirectModalComponent,
          ),
      },
    ],
  },
  {
    path: 'purchase/:id',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/purchase-modal/purchase-modal.component').then((m) => m.PurchaseModalComponent),
      },
    ],
  },
  {
    path: 'boosty-payment-status',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [authGuard, NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@rpgc-ai/lazy-blocks/boosty-payment-status/boosty-payment-status.component').then((m) => m.BoostyPaymentStatusComponent),
      },
    ],
  },
  {
    path: 'top-up-balance',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/top-up-balance/top-up-balance.component').then((m) => m.TopUpBalanceComponent),
      },
    ],
  },
  // Обработка несуществующих модальных окон
  {
    path: '**',
    outlet: 'modal',
    loadComponent: () => import('@rpgc-ai/lazy-blocks/modal/modal.component').then((m) => m.ModalComponent),
    canActivate: [NoSsrModalGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/lazy-blocks/nonexisting/non-existing.component').then((m) => m.NonExistingComponent),
      },
    ],
  },
];
